/*
  Floss Offer LP (sections/fo-*.liquid)
  - Offer data (flavors → 4-pack variants, Recharge plans and their prices)
    comes from the first [data-fo-offer] JSON (snippets/fo-data.liquid).
  - [data-fo-add="hero" | "sticky"] adds the default 4-pack subscription
    (default flavor, default plan). [data-fo-add="buy_box"] adds the buy box
    selection. Posts to /cart/add.js (with selling_plan when subscribing),
    syncs the cart count and opens UpCart / the theme mini-cart.
    Subscriptions add the free scraper in the same request (_free_gift_scraper);
    the gift rule in layout/theme.liquid keeps it at one line.
  - Mobile sticky bar shows below 760px once the hero is out of view.
  - FAQ: one <details> open at a time.
  - Reviews: real Okendo reviews only; the section stays hidden otherwise.
  - Analytics: every event goes to window.dataLayer (event "fo_*"),
    Shopify.analytics.publish (custom pixels) and a "fo:track" DOM event,
    with the page variant (swap / holiday / leaf).
*/
(() => {
  if (window.foOfferReady) return;
  window.foOfferReady = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const readJSON = (el) => {
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  };

  const getOffer = () => readJSON(document.querySelector('[data-fo-offer]')) || { flavors: [], plans: [] };

  const pageVariant = () => {
    const hero = document.querySelector('[data-fo-hero]');
    return (hero && hero.dataset.foVariant) || 'unknown';
  };

  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const track = (name, detail = {}) => {
    const payload = { page_variant: pageVariant(), ...detail };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: `fo_${name}`, ...payload });
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(`fo_${name}`, payload);
      }
    } catch (e) { /* analytics must never break the page */ }
    document.dispatchEvent(new CustomEvent('fo:track', { detail: { name, ...payload } }));
  };

  /* ---------- Cart ---------- */

  const openCartDrawer = () => {
    if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    if (typeof window.upcartOpenCart === 'function') {
      window.upcartOpenCart();
      return;
    }
    const store = window.Alpine && window.Alpine.store && window.Alpine.store('xMiniCart');
    if (store) {
      if (typeof store.reLoad === 'function') store.reLoad();
      store.openCart();
      return;
    }
    window.location.href = `${root}cart`;
  };

  const planFor = (offer, months) => {
    const plans = offer.plans || [];
    return plans.find((p) => p.months === months) || plans[plans.length - 1] || null;
  };

  const flavorFor = (offer, name) => (offer.flavors || []).find((f) => f.name === name) || (offer.flavors || [])[0] || null;

  // { flavor, plan: 'sub' | 'one', months } -> /cart/add.js item
  const buildItem = (offer, selection) => {
    const flavor = flavorFor(offer, selection.flavor);
    if (!flavor || !flavor.available) return null;
    const item = { id: flavor.variant, quantity: 1 };
    if (selection.plan === 'sub') {
      const plan = planFor(offer, selection.months);
      if (plan && flavor.plans && flavor.plans[plan.id] !== undefined) item.selling_plan = plan.id;
    }
    return item;
  };

  const addToCart = async (button, selection, source) => {
    const offer = getOffer();
    const scope = button.closest('[data-fo-buy], [data-fo-hero], [data-fo-sticky]') || document;
    const errorEl = scope.querySelector('[data-fo-error]');
    const errorText = (scope.dataset && scope.dataset.errorText) || 'Something went wrong. Please try again.';
    const item = buildItem(offer, selection);
    if (errorEl) errorEl.textContent = '';
    if (!item) {
      if (errorEl) errorEl.textContent = errorText;
      return;
    }

    // Subscription: add the floss and the free scraper together in one request.
    const items = [item];
    if (item.selling_plan && offer.scraperVariant) {
      items.push({ id: offer.scraperVariant, quantity: 1, properties: { _free_gift_scraper: 'true' } });
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items }),
      });
      // Shopify can answer with an HTML page (e.g. 429 rate limit): don't crash on it.
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status === 422) throw new Error(data.description || data.message || errorText);

      track('add_to_cart', {
        source,
        flavor: selection.flavor,
        plan: item.selling_plan ? 'subscribe' : 'one_time',
        frequency_months: item.selling_plan ? selection.months : null,
        variant_id: item.id,
        selling_plan_id: item.selling_plan || null,
      });

      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);
      if (cart) {
        if (typeof window.syncCartIconBubbles === 'function') window.syncCartIconBubbles(cart.item_count);
        document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      openCartDrawer();
    } catch (error) {
      console.error('fo add to cart:', error);
      if (errorEl) errorEl.textContent = (error && error.message) || errorText;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  /* ---------- Radio groups (arrow keys, roving tabindex) ---------- */

  const bindRadioGroup = (buttons, onPick) => {
    buttons.forEach((button, index) => {
      button.addEventListener('click', () => onPick(button));
      button.addEventListener('keydown', (event) => {
        const delta = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!delta) return;
        event.preventDefault();
        let next = index;
        for (let i = 0; i < buttons.length; i++) {
          next = (next + delta + buttons.length) % buttons.length;
          if (!buttons[next].disabled) break;
        }
        buttons[next].focus();
        onPick(buttons[next]);
      });
    });
  };

  const setChecked = (buttons, active) => {
    buttons.forEach((b) => {
      const on = b === active;
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on ? 0 : -1;
    });
  };

  /* ---------- Buy box ---------- */

  const initBuyBox = (box) => {
    if (box.dataset.foReady) return;
    box.dataset.foReady = 'true';

    const offer = readJSON(box.querySelector('[data-fo-offer]')) || getOffer();
    const hints = readJSON(box.querySelector('[data-fo-hints]')) || {};
    const flavorButtons = [...box.querySelectorAll('[data-fo-flavor]')];
    const planButtons = [...box.querySelectorAll('[data-fo-plan]')];
    const freqButtons = [...box.querySelectorAll('[data-fo-months]')];
    const freqWrap = box.querySelector('[data-fo-freq]');
    const hintEl = box.querySelector('[data-fo-hint]');
    const ctaEl = box.querySelector('[data-fo-cta]');
    const cta = box.querySelector('[data-fo-add="buy_box"]');

    const checkedFlavor = flavorButtons.find((b) => b.getAttribute('aria-checked') === 'true');
    const checkedFreq = freqButtons.find((b) => b.getAttribute('aria-checked') === 'true');
    const state = {
      flavor: checkedFlavor ? checkedFlavor.dataset.foFlavor : offer.defaultFlavor,
      plan: (offer.plans || []).length ? 'sub' : 'one',
      months: checkedFreq ? Number(checkedFreq.dataset.foMonths) : Number(box.dataset.defaultMonths) || offer.defaultMonths,
    };

    // Unavailable flavors can't be picked.
    flavorButtons.forEach((b) => {
      const f = flavorFor(offer, b.dataset.foFlavor);
      if (f && !f.available) b.disabled = true;
    });

    const render = () => {
      const flavor = flavorFor(offer, state.flavor);
      const plan = planFor(offer, state.months);
      const list = flavor ? flavor.price : 0;
      const sub = flavor && plan && flavor.plans && flavor.plans[plan.id] !== undefined ? flavor.plans[plan.id] : list;
      const isSub = state.plan === 'sub';
      const total = isSub ? sub : list;

      setChecked(flavorButtons, flavorButtons.find((b) => b.dataset.foFlavor === state.flavor));
      setChecked(planButtons, planButtons.find((b) => b.dataset.foPlan === state.plan));
      setChecked(freqButtons, freqButtons.find((b) => Number(b.dataset.foMonths) === (plan && plan.months)));
      if (freqWrap) freqWrap.hidden = !isSub;
      if (hintEl) hintEl.textContent = (plan && hints[plan.months]) || '';

      box.querySelectorAll('[data-fo-flavor-name]').forEach((el) => { el.textContent = state.flavor; });
      box.querySelectorAll('[data-fo-flavor-img]').forEach((img) => {
        const on = img.dataset.foFlavorImg === state.flavor;
        img.classList.toggle('is-active', on);
        if (on) img.removeAttribute('aria-hidden');
        else img.setAttribute('aria-hidden', 'true');
      });
      box.querySelectorAll('[data-fo-price="list"]').forEach((el) => { el.textContent = money(list); });
      box.querySelectorAll('[data-fo-price="sub"]').forEach((el) => { el.textContent = money(sub); });
      box.querySelectorAll('[data-fo-price="save"]').forEach((el) => { el.textContent = money(list - sub); });
      box.querySelectorAll('[data-fo-price="total"]').forEach((el) => { el.textContent = money(total); });
      box.querySelectorAll('[data-fo-show]').forEach((el) => { el.hidden = el.dataset.foShow !== state.plan; });

      const template = isSub ? box.dataset.ctaSub : box.dataset.ctaOne;
      if (ctaEl && template) ctaEl.textContent = template.replace('[price]', money(total));
      if (cta) cta.disabled = !flavor || !flavor.available;
    };

    bindRadioGroup(flavorButtons, (b) => {
      if (b.disabled || state.flavor === b.dataset.foFlavor) return;
      state.flavor = b.dataset.foFlavor;
      render();
      track('flavor_select', { flavor: state.flavor });
    });
    bindRadioGroup(planButtons, (b) => {
      if (state.plan === b.dataset.foPlan) return;
      state.plan = b.dataset.foPlan;
      render();
      track('plan_select', { plan: state.plan === 'sub' ? 'subscribe' : 'one_time' });
    });
    bindRadioGroup(freqButtons, (b) => {
      const months = Number(b.dataset.foMonths);
      if (state.months === months) return;
      state.months = months;
      render();
      track('frequency_select', { frequency_months: months });
    });

    if (cta) cta.addEventListener('click', () => addToCart(cta, { ...state }, 'buy_box'));

    // Flavor images: download and decode them well before the buy box is on
    // screen (and right away if a flavor is touched first), so the swap is
    // instant instead of waiting on the network / image decode.
    const flavorImages = [...box.querySelectorAll('[data-fo-flavor-img]')];
    if (flavorImages.length) {
      let warmed = false;
      const warm = () => {
        if (warmed) return;
        warmed = true;
        flavorImages.forEach((img) => {
          img.loading = 'eager';
          if (img.decode) img.decode().catch(() => {});
        });
      };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            io.disconnect();
            warm();
          }
        }, { rootMargin: '1200px 0px' });
        io.observe(box);
      } else {
        warm();
      }
      flavorButtons.forEach((b) => {
        b.addEventListener('pointerenter', warm, { once: true });
        b.addEventListener('touchstart', warm, { once: true, passive: true });
        b.addEventListener('focus', warm, { once: true });
      });
    }

    render();
  };

  /* ---------- Hero + sticky CTAs (default offer) ---------- */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-fo-add="hero"], [data-fo-add="sticky"]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const offer = getOffer();
    addToCart(button, { flavor: offer.defaultFlavor, plan: 'sub', months: offer.defaultMonths }, button.dataset.foAdd);
  });

  /* ---------- Star rating → Okendo all-reviews dialog (as on hp-hero) ---------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-fo-reviews-open]');
    if (!trigger) return;
    const reviewsTab = document.querySelector('.oke-reviewsTab');
    if (!reviewsTab) return; // Okendo not loaded: follow the fallback link
    event.preventDefault();
    reviewsTab.click();
    track('reviews_open', { source: 'hero_rating' });
  });

  /* ---------- Mobile sticky bar ---------- */

  const initSticky = () => {
    const bar = document.querySelector('[data-fo-sticky]');
    const hero = document.querySelector('[data-fo-hero]');
    if (!bar || bar.dataset.foReady) return;
    bar.dataset.foReady = 'true';
    const mobile = window.matchMedia('(max-width: 759.98px)');
    let heroGone = false;

    const update = () => {
      const show = mobile.matches && heroGone;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelectorAll('button, a').forEach((el) => { el.tabIndex = show ? 0 : -1; });
      document.body.classList.toggle('fo-sticky-on', show);
    };

    if (hero && 'IntersectionObserver' in window) {
      // "Gone" only once the hero's bottom edge is above the viewport.
      new IntersectionObserver(([entry]) => {
        heroGone = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        update();
      }).observe(hero);
    } else {
      heroGone = true;
    }
    if (mobile.addEventListener) mobile.addEventListener('change', update);
    update();
  };

  /* ---------- FAQ: one open at a time ---------- */

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    const group = details.closest('[data-fo-accordion]');
    if (!group) return;
    group.querySelectorAll('details[open]').forEach((other) => { if (other !== details) other.open = false; });
  }, true);

  /* ---------- Reviews (Okendo) ---------- */

  const STAR = '<svg viewBox="0 0 20 19" aria-hidden="true" focusable="false"><path fill="currentColor" d="M10 0l2.94 6.1 6.56.86-4.8 4.62 1.18 6.6L10 15.03 4.12 18.18l1.18-6.6L.5 6.96l6.56-.86z"/></svg>';

  const textNode = (tag, className, text) => {
    const el = document.createElement(tag);
    el.className = className;
    el.textContent = text;
    return el;
  };

  const initReviews = async (section) => {
    if (section.dataset.foReady) return;
    section.dataset.foReady = 'true';
    const { subscriber, productId } = section.dataset;
    if (!subscriber || !productId) return;
    const minRating = Number(section.dataset.minRating) || 5;
    const limit = Number(section.dataset.limit) || 3;
    const verifiedOnly = section.dataset.verifiedOnly === 'true';
    const list = section.querySelector('[data-fo-reviews-list]');

    try {
      const reviews = [];
      let url = `https://api.okendo.io/v1/stores/${encodeURIComponent(subscriber)}/products/shopify-${encodeURIComponent(productId)}/reviews?limit=25&orderBy=rating%20desc`;
      for (let page = 0; url && page < 4 && reviews.length < limit; page++) {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Okendo ${res.status}`);
        const data = await res.json();
        (data.reviews || []).forEach((r) => {
          const verified = r.reviewer && r.reviewer.isVerified;
          if (r.rating >= minRating && r.body && (!verifiedOnly || verified)) reviews.push(r);
        });
        url = data.nextUrl ? `https://api.okendo.io/v1${data.nextUrl}` : null;
      }
      if (!reviews.length) return;

      reviews.slice(0, limit).forEach((r) => {
        const li = document.createElement('li');
        li.className = 'fo-review';
        const stars = document.createElement('div');
        stars.className = 'fo-review__stars';
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `${r.rating} out of 5 stars`);
        stars.innerHTML = STAR.repeat(Math.round(r.rating));
        li.appendChild(stars);
        if (r.title) li.appendChild(textNode('p', 'fo-review__title', r.title));
        li.appendChild(textNode('p', 'fo-review__body', r.body));
        const by = textNode('p', 'fo-review__by', (r.reviewer && r.reviewer.displayName) || '');
        if (r.reviewer && r.reviewer.isVerified) {
          by.appendChild(textNode('span', 'fo-review__verified', ` · ${section.dataset.verifiedLabel || '✓ Verified buyer'}`));
        }
        li.appendChild(by);
        list.appendChild(li);
      });
      section.hidden = false;
    } catch (error) {
      console.warn('fo reviews:', error);
    }
  };

  /* ---------- Init ---------- */

  const init = (scope = document) => {
    scope.querySelectorAll('[data-fo-buy]').forEach(initBuyBox);
    scope.querySelectorAll('[data-fo-reviews]').forEach(initReviews);
    initSticky();
  };

  const start = () => {
    init();
    track('page_view');
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
